// The car park and street south of the office, where people arrive.
import type { Rect } from "./types";

/** The kinds of bay the car park paints. People who walk in need none. */
export type TownParkingBayKind = "car" | "motorbike" | "bicycle";

export type TownParkingBay = Readonly<{
  kind: TownParkingBayKind;
  /** Bay number within its kind, west to east from 0. */
  index: number;
  /** Centre of the bay. Vehicles park nose north. */
  x: number;
  z: number;
  width: number;
  depth: number;
}>;

/**
 * Bays along the north edge of the car park, west to east: cars, the bicycle rack, then motorbikes, the most common
 * ride, nearest the driveway and the footpath to the door.
 */
const BAY_ROWS = [
  { kind: "car", count: 7, width: 1.6, depth: 2.6 },
  { kind: "bicycle", count: 6, width: .6, depth: 1.6 },
  { kind: "motorbike", count: 8, width: .8, depth: 1.8 },
] as const satisfies readonly Readonly<{ kind: TownParkingBayKind; count: number; width: number; depth: number }>[];

const LOT_WEST = -31, LOT_EAST = -7, LOT_NORTH = 27, STREET_NORTH = 33;
/** Bays start a little in from the west kerb and the north lawn; each kind leaves a gap before the next. */
const BAY_START_X = -30.6, BAY_NORTH = 27.2, ROW_GAP = .4;

function bays(): TownParkingBay[] {
  const all: TownParkingBay[] = [];
  let x = BAY_START_X;
  for (const row of BAY_ROWS) {
    for (let index = 0; index < row.count; index++) {
      all.push({ kind: row.kind, index, x: x + row.width / 2, z: BAY_NORTH + row.depth / 2, width: row.width, depth: row.depth });
      x += row.width;
    }
    x += ROW_GAP;
  }
  return all;
}

/**
 * Where everything south of the office sits: the car park in the south-west corner, its driveway onto the street,
 * the footpath from the car park to the entrance walk, the sidewalk and the two-lane street along the campus edge.
 * Westbound traffic keeps to the north lane, nearer the office, as in Viet Nam.
 */
export const TOWN_CAR_PARK = {
  lot: { x0: LOT_WEST, z0: LOT_NORTH, x1: LOT_EAST, z1: 32 },
  /** The lane through the car park, south of the bays. */
  aisleZ: 31,
  driveway: { x0: -10, z0: 32, x1: -7, z1: STREET_NORTH },
  footpath: { x0: -7, z0: 30.5, x1: -2.5, z1: 31.5 },
  sidewalk: { x0: -33, z0: 32, x1: 33, z1: STREET_NORTH },
  street: { x0: -33, z0: STREET_NORTH, x1: 33, z1: 37 },
  /** Centre of the westbound and eastbound lanes. */
  westboundZ: 34,
  eastboundZ: 36,
  bays: bays(),
} as const satisfies Readonly<{
  lot: Rect; aisleZ: number; driveway: Rect; footpath: Rect; sidewalk: Rect; street: Rect;
  westboundZ: number; eastboundZ: number; bays: readonly TownParkingBay[];
}>;

export function townParkingBays(kind: TownParkingBayKind): readonly TownParkingBay[] {
  return TOWN_CAR_PARK.bays.filter((bay) => bay.kind === kind);
}

/** Painted lines are four texels wide at the gameplay density, thinner than the floor plan lattice, so renderers draw them. */
export const TOWN_PAINT_WIDTH = .125;

/**
 * Bay lines: one between every pair of neighbouring bays and one at each end of a row, from the bay's north edge to
 * its south edge.
 */
export function townParkingLines(): readonly Rect[] {
  const lines: Rect[] = [];
  for (const kind of ["car", "motorbike", "bicycle"] as const) {
    const row = townParkingBays(kind);
    const first = row[0]!;
    const edges = [first.x - first.width / 2, ...row.map((bay) => bay.x + bay.width / 2)];
    const z0 = first.z - first.depth / 2, z1 = first.z + first.depth / 2;
    for (const x of edges) lines.push({ x0: x - TOWN_PAINT_WIDTH / 2, z0, x1: x + TOWN_PAINT_WIDTH / 2, z1 });
  }
  return lines;
}

/** Yellow dashes `length` long every `step` down the middle of the street, leaving the zebra crossing clear. */
export function townStreetDashes(step = 3, length = 1.5): readonly Rect[] {
  const { street } = TOWN_CAR_PARK;
  const z = (street.z0 + street.z1) / 2, half = TOWN_PAINT_WIDTH / 2;
  const dashes: Rect[] = [];
  for (let x = street.x0 + 1; x + length <= street.x1; x += step) {
    if (x + length > -3 && x < 3) continue;
    dashes.push({ x0: x, z0: z - half, x1: x + length, z1: z + half });
  }
  return dashes;
}
